import { Role } from '@liveline/ll_node_common/lib/Permissions/types';
import type { FacilityUserRole } from '../services/apiTypes';
import { ScreenKey } from './screenKeys';

/**
 * LT-3810 — screens that a facility ROLE also opens, beside their grant: a user holding one of these roles
 * on the selected facility (an active facility_users row) may open the screen whatever their screens say.
 * Checked at the same two generic gate sites as SCREEN_PERMISSION_ALIAS (`menuItemIsDisabled` and
 * `ScreenPermissionGate`, through `screenOpenToFacilityRole`). A role, not a grant, because screens have no
 * working grant path (see ALWAYS_VISIBLE_SCREENS): a facility Admin edits that facility's process insight
 * overrides, and the page itself keeps template edits LivelineControls-only.
 */
export const SCREEN_ROLE_ALLOW: Readonly<Record<string, readonly Role[]>> = {
    [ScreenKey.PROCESS_INSIGHT_TEMPLATES]: [Role.ADMIN],
};

/**
 * LT-3810 — the signed-in user's facility_users rows, as login stored them (`authReducer` persists the
 * user, without tokens, under `user`; `routes.js` and `FacilityRolesGate` read it the same way). The
 * CASL ability built from these rows carries each role's grants but not the role's name, so a check that
 * is about the role itself reads it here. Empty when nothing is stored or it does not parse.
 */
export const storedFacilityRoles = (): FacilityUserRole[] => {
    try {
        const permissions = JSON.parse(localStorage.getItem('user') || '{}')?.permissions;
        return Array.isArray(permissions) ? permissions : [];
    } catch {
        return [];
    }
};

/**
 * Does the user hold `role`, on an active row, for this facility? Pass the `liveline_facility_id` UUID
 * the rows carry, not the numeric facility id. False while the facility is still loading.
 */
export const hasFacilityRole = (
    role: Role,
    livelineFacilityId: string | undefined | null,
    rows: FacilityUserRole[] = storedFacilityRoles(),
): boolean =>
    !!livelineFacilityId
    && rows.some((r) => !!r && r.isActive && r.facilityId === livelineFacilityId && r.role === role);

/**
 * Facility Admin — the front half of console_server's override gate for process insight (LT-3810):
 * the Admin role on a facility may read and change that facility's overrides, not the templates.
 */
export const isFacilityAdmin = (livelineFacilityId: string | undefined | null, rows?: FacilityUserRole[]): boolean =>
    hasFacilityRole(Role.ADMIN, livelineFacilityId, rows);

/** Is this screen opened by a role the user holds on this facility (SCREEN_ROLE_ALLOW)? */
export const screenOpenToFacilityRole = (
    screenKey: string,
    livelineFacilityId: string | undefined | null,
    rows?: FacilityUserRole[],
): boolean =>
    (SCREEN_ROLE_ALLOW[screenKey] ?? []).some((role) => hasFacilityRole(role, livelineFacilityId, rows));
